import Recorder from 'opus-recorder';
import React, {
  createContext, useState, useContext, useRef, useCallback,
} from 'react';
import normalizeOggOpus, { isValidOggOpus } from '../utils/normalizeOggOpus';

const RecorderContext = createContext({
  startRecorder: () => null,
  stopRecorder: () => null,
  mediaBlobUrl: null,
  clearBlobUrl: () => null,
  recordingError: false,
});

export const useRecorder = () => useContext(RecorderContext);

export const RecorderProvider = ({ children }) => {
  const [mediaBlobUrl, setMediaBlobUrl] = useState('');
  const [recordingError, setRecordingError] = useState(false);

  const recorderRef = useRef(null);
  const blobUrlRef = useRef('');

  // A blob URL keeps its recording in memory until revoked, and the tab can stay
  // open for weeks, so the previous one is released whenever it is replaced.
  const setBlobUrl = useCallback((url) => {
    if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    blobUrlRef.current = url;
    setMediaBlobUrl(url);
  }, []);

  // Every Recorder spawns an AudioContext and compiles the encoder worklet, and
  // never releases them, so a single instance is created on first use and reused.
  const getRecorder = useCallback(() => {
    if (!recorderRef.current) {
      const recorder = new Recorder({ encoderPath: `${window.location.origin}/encoderWorker.min.js` });
      recorder.ondataavailable = (typedArray) => {
        const audio = normalizeOggOpus(typedArray);
        setRecordingError(!isValidOggOpus(audio));
        const dataBlob = new Blob([audio], { type: 'audio/ogg' });
        setBlobUrl(URL.createObjectURL(dataBlob));
      };
      recorderRef.current = recorder;
    }
    return recorderRef.current;
  }, [setBlobUrl]);

  const clearBlobUrl = useCallback(() => setBlobUrl(''), [setBlobUrl]);

  const startRecorder = () => {
    clearBlobUrl();
    setRecordingError(false);
    getRecorder().start();
  };

  const stopRecorder = () => {
    if (recorderRef.current) recorderRef.current.stop();
  };

  return (
    <RecorderContext.Provider value={{
      startRecorder, stopRecorder, mediaBlobUrl, clearBlobUrl, recordingError,
    }}
    >
      {children}
    </RecorderContext.Provider>
  );
};
